/**
 * ProviderKeys (admin only) — enter the provider API keys the product uses: the
 * speech-to-text vendors (after-call + the engine A/B compare screen) and the
 * Anthropic (Claude) key powering after-call scoring AND live coaching.
 *
 * Security: keys are sent to the admin-gated server, stored ENCRYPTED server-side
 * (provider_secrets), and NEVER returned to the browser — this screen only ever
 * shows "set / ••••last4 / source". Entering a new value replaces the stored key;
 * clearing removes it (falling back to an env var if one is configured).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ProviderSecretStatus } from "@/types";
import { PageHeader, Button, Card, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";

const SECRETS_KEY = ["admin", "stt-secrets"] as const;

const PROVIDER_LABELS: Record<string, string> = {
  assemblyai: "AssemblyAI",
  deepgram: "Deepgram",
  speechmatics: "Speechmatics",
  gladia: "Gladia (Whisper + PyAnnote)",
  elevenlabs: "ElevenLabs Scribe",
  anthropic: "Anthropic (Claude)",
};

function KeyRow({ secret }: { secret: ProviderSecretStatus }) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (api_key: string) =>
      api.sttSetSecret({ provider: secret.provider, api_key }),
    onSuccess: (data) => {
      qc.setQueryData(SECRETS_KEY, { secrets: data.secrets });
      setValue("");
      setSaved(true);
    },
  });

  const label = PROVIDER_LABELS[secret.provider] ?? secret.provider;
  const status =
    secret.source === "stored"
      ? { tone: "primary" as const, text: `Stored ••••${secret.last4 ?? ""}` }
      : secret.source === "env"
        ? { tone: "muted" as const, text: `From env ••••${secret.last4 ?? ""}` }
        : { tone: "muted" as const, text: "Not set" };

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{label}</h2>
        <Badge tone={status.tone}>{status.text}</Badge>
      </div>
      <div className="flex items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          <span className="sr-only">{label} API key</span>
          <input
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (saved) setSaved(false);
            }}
            placeholder={secret.is_set ? "Enter a new key to replace…" : "Paste API key…"}
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        <Button
          onClick={() => save.mutate(value)}
          disabled={save.isPending || value.trim().length === 0}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        {secret.source === "stored" && (
          <Button
            variant="ghost"
            onClick={() => {
              if (saved) setSaved(false);
              save.mutate("");
            }}
            disabled={save.isPending}
          >
            Clear
          </Button>
        )}
      </div>
      <div className="flex items-center gap-3 text-sm">
        {saved && <span className="font-medium text-emerald-600 dark:text-emerald-400">Saved.</span>}
        {save.isError && <span className="text-red-600">Couldn&apos;t save. Try again.</span>}
      </div>
    </Card>
  );
}

export function ProviderKeys() {
  const query = useQuery({
    queryKey: SECRETS_KEY,
    queryFn: ({ signal }) => api.sttSecrets(signal),
  });

  if (query.isLoading) {
    return (
      <div className="flex flex-1 flex-col">
        <PageHeader title="API keys" />
        <LoadingState label="Loading API keys…" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="flex flex-1 flex-col">
        <PageHeader title="API keys" />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title="API keys"
        subtitle="Provider keys for transcription (STT) and Claude (Anthropic) — used by after-call scoring AND live coaching. Keys are encrypted server-side and never shown again."
      />
      <div className="grid gap-4 p-5 md:grid-cols-2">
        {query.data.secrets.map((s) => (
          <KeyRow key={s.provider} secret={s} />
        ))}
      </div>
    </div>
  );
}
